<template>
  <div class="highlight-card" :class="{ 'is-processing': isProcessing }">
    <div class="highlight-thumb">
      <img
        v-if="video.thumbnail && !isProcessing"
        class="highlight-thumb-img"
        :src="video.thumbnail"
        :alt="video.title"
      />
      <video
        v-else-if="video.videoUrl && !isProcessing"
        class="highlight-thumb-img"
        :src="`${video.videoUrl}#t=0.5`"
        preload="metadata"
        muted
        playsinline
        tabindex="-1"
      ></video>
      <div v-else class="highlight-thumb-placeholder"></div>

      <span v-if="!isProcessing && !purchased" class="gems-badge">
        <img src="/assets/event-highlights/gems.svg" alt="" />
        {{ video.points }}
      </span>

      <a
        v-if="!isProcessing"
        class="highlight-play"
        href="javascript:void(0)"
        @click="$emit('play', video)"
      >
        <img src="/assets/event-highlights/play_btn.svg" alt="Play" />
      </a>

      <!-- Processing overlay: a highlight that is still being produced
           (backend status "pending") has no playable video yet, so cover the
           thumb with an in-flight state on refresh. -->
      <div v-if="isProcessing" class="hl-status-overlay">
        <div class="hl-status-spinner" aria-hidden="true"></div>
        <strong>Processing your highlight…</strong>
        <span>This can take a few minutes. Check back shortly.</span>
      </div>
    </div>

    <h4 class="highlight-title">{{ video.title }}</h4>

    <div class="hl-meta">
      <div class="hl-meta-item">
        <img src="/assets/event-highlights/calender.svg" alt="" />
        <span>
          <small>Event Date</small>
          {{ video.eventDate }}
        </span>
      </div>
      <div class="hl-meta-item">
        <img src="/assets/event-highlights/clock.svg" alt="" />
        <span>
          <small>{{ isProcessing ? "Status:" : "Expiry Date:" }}</small>
          {{ isProcessing ? "Processing" : video.expiryDate }}
        </span>
      </div>
    </div>

    <div class="highlight-actions">
      <span v-if="isProcessing" class="hl-btn hl-btn--download is-disabled">
        <span>Processing</span>
      </span>
      <a
        v-else
        href="javascript:void(0)"
        class="hl-btn hl-btn--download"
        @click="$emit('download', video)"
      >
        <span>Download</span>
      </a>
      <a
        v-if="purchased && !isProcessing"
        class="highlight-delete-btn"
        href="javascript:void(0)"
        aria-label="Delete"
        @click="$emit('delete', video)"
      >
        <img src="/assets/event-highlights/delete_btn.svg" alt="Delete" />
      </a>
    </div>
  </div>
</template>

<script>
import "@/assets/css/event-highlights.css";

export default {
  name: "HighlightCard",
  props: {
    video: {
      type: Object,
      required: true,
    },
    purchased: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isProcessing() {
      return this.video.status === "processing";
    },
  },
};
</script>

<style scoped>
.highlight-card {
  position: relative;
  display: flex;
  flex-direction: column;
  /* fill the (equal-height) column but leave room for the row gap below —
     height:100% alone would swallow the margin and butt rows together */
  height: calc(100% - 24px);
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 24px;
}
.highlight-card.is-processing {
  border-color: #d0d0d0;
  background: #fafafa;
}
.highlight-thumb {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #d9d9d9;
}
.highlight-thumb-img,
.highlight-thumb-placeholder {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
}

.highlight-thumb .gems-badge {
  position: absolute;
  top: 12px;
  left: 12px;
}
.highlight-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
}
.highlight-play img {
  width: 100%;
  height: 100%;
  display: block;
}
.highlight-title {
  font-size: 18px;
  font-weight: 700;
  margin: 14px 0 12px;
  color: #1a1a1a;
}
.hl-meta {
  margin-bottom: 16px;
}

.hl-meta-item {
  flex: 1 1 0;
  min-width: 0;
}
.highlight-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
}
.highlight-actions .hl-btn {
  flex: 1 1 auto;
  font-size: 18px;
}
.highlight-actions .hl-btn.is-disabled {
  cursor: not-allowed;
  opacity: 0.65;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* status overlay sits over the thumb area for a still-processing highlight */
.hl-status-overlay {
  position: absolute;
  inset: 0;
  border-radius: 8px;
  background: rgba(20, 20, 20, 0.78);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 14px;
  pointer-events: none;
}
.hl-status-overlay strong {
  font-size: 15px;
  font-weight: 700;
}
.hl-status-overlay span {
  font-size: 12px;
  opacity: 0.85;
  max-width: 280px;
}
.hl-status-spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: #fff;
  animation: hl-spin 0.9s linear infinite;
}
@keyframes hl-spin {
  to {
    transform: rotate(360deg);
  }
}
.highlight-delete-btn {
  flex: 0 0 auto;
  height: 46px;
  aspect-ratio: 1 / 1;
  display: block;
}
.highlight-delete-btn img {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
